import React, { HTMLAttributes, ReactElement } from 'react'

import { RetainBehaviorModifier, RetainSizeModifier } from './constants/modifiers'

interface RetainProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactElement[] | ReactElement
    sizeModifier?: RetainSizeModifier
    behaviorModifiers?: RetainBehaviorModifier[]
    behaviorOnly?: boolean
}

/**
 * Retain component
 *
 * @description
 * The Retain component is used to retain the size of it's children horizontally in two ways.
 * 1. By preventing the children from growing larger than a certain size, using max-inline-size.
 * 2. By preventing the children from touching the edges of the parent, using padding-inline.
 *    This padding is variable depending on the screen size. Optically, padding looks the same size on
 *    all screen sizes, but in reality it is smaller on mobile screens for example.
 *
 * The Retain component does nothing in the block direction.
 *
 * @param {RetainProps} props - The props of the Retain component.
 * @param {RetainSizeModifier} [props.sizeModifier] - The size modifier that defines the max inline size of the Retainer.
 * @param {RetainBehaviorModifier[]} [props.behaviorModifiers] - The behavior modifiers that define the behavior of the Retainer.
 * @param {boolean} [props.behaviorOnly] - When behaviorOnly is true, existing custom properties (css) of the o-retain class
 *    will be used, and supplemented with the provided behavior modifiers. If no behavior modifiers are provided,
 *    the children will just be returned. This is useful when behavior modifiers are provided conditionally.
 * @param {string} [props.className] - The className that will be added to the classes of the Retain component.
 *    if behaviorOnly is true but no behaviorModifiers where provided,
 *    the children will be wrapped within a div with this className.
 *
 * @example
 * <Retain sizeModifier={RetainSizeModifier.LARGE} behaviorModifiers={[RetainBehaviorModifier.NO_PADDING]}>
 *   <div>content</div>
 * </Retain>
 */
export const Retain = ({
    children,
    sizeModifier,
    behaviorModifiers = [],
    behaviorOnly,
    className,
    ...props
}: RetainProps): ReactElement => {
    /* if the user intents to only use the behavior modifiers, but did not provide valid behavior modifiers,
     * then return the children in a div with the provided className */
    const hasBehaviorModifiers = !behaviorModifiers.filter((i) => !!i).length
    if (behaviorOnly && hasBehaviorModifiers && !!className) {
        return <div className={className}>{children}</div>
    }
    if (behaviorOnly && hasBehaviorModifiers) {
        return <>{children}</>
    }

    const modifierPrefix = ' o-retain--'
    const modifiers: string[] = [...behaviorModifiers]
    if (sizeModifier && !behaviorOnly) modifiers.unshift(sizeModifier)

    const classes = (behaviorOnly ? '' : 'o-retain') + modifiers.map((mod) => modifierPrefix + mod).join(' ')
    const addClasses = className ? ' ' + className : ''
    return <div className={classes + addClasses}>{children}</div>
}
